
'use client'

import { signOut, useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import { publicRoutes } from '../auth.public-routes'

export const AuthGuard = () => {
  const session = useSession()
  const pathname = usePathname()
  const qcIdRef = useRef<string>(undefined)

  const reloadOnUserChange = useCallback(() => {
    if (!session.data?.qcId) {
      return
    }

    if (
      qcIdRef.current !== undefined &&
      qcIdRef.current !== session.data?.qcId &&
      session.data?.flow !== 'impersonation'
    ) {
      qcIdRef.current = session.data?.qcId

      setTimeout(() => {
        window.location.href = '/'
      }, 1000)
    } else {
      qcIdRef.current = session.data?.qcId
    }
  }, [session.data?.qcId, session.data?.flow])

  const signoutIfUnauthenticated = useCallback(async () => {
    const isLoading = session.status === 'loading'
    const isUnauthenticated = session.status === 'unauthenticated'
    const isProtectedRoute = !publicRoutes.includes(pathname)
    const hasSessionError = !!session?.data?.error
    const logoutUrl = session.data?.logoutUrl ?? '/signed-out'

    if (!isProtectedRoute || isLoading) {
      return
    }

    if ((isUnauthenticated && isProtectedRoute) || hasSessionError) {
      // Timer is here to prevent a race condition between B2C and
      // auth js when signing out.
      await new Promise(resolve => setTimeout(resolve, 1000))
      await signOut({ redirect: false })
      window.location.href = logoutUrl
    }

    if (session.data?.terminateAt && Date.now() > session.data.terminateAt) {
      await signOut({ redirect: false })
      window.location.href = logoutUrl
    }
    // oxlint-disable-next-line react/exhaustive-deps -- qcId is only used to detect account changes.
  }, [
    session.status,
    session.data?.error,
    session.data?.terminateAt,
    session.data?.logoutUrl,
    session.data?.qcId,
    pathname,
  ])

  useEffect(() => {
    reloadOnUserChange()
  }, [reloadOnUserChange])

  useEffect(() => {
    signoutIfUnauthenticated()
  }, [signoutIfUnauthenticated])

  return null
}
